import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Shield, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { superadminService } from '@/services/superadminService';
import { useAdminAuthStore } from '@/stores/adminAuthStore';
import type { AdminWithRestaurants, AdminRole } from '@/types/admin';
import { AdminRoleLabels } from '@/types/admin';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AdminPageShell, PageHeader, PageLoading } from '@/components/admin/shell';
import { AdminButton } from '@/components/admin/shell/AdminButton';

interface Restaurant {
  id: string;
  name: string;
}

interface AdminFormData {
  email: string;
  password: string;
  name: string;
  role: AdminRole;
  super_admin: boolean;
  restaurant_ids: string[];
}

const emptyFormData: AdminFormData = {
  email: '',
  password: '',
  name: '',
  role: 'owner',
  super_admin: false,
  restaurant_ids: [],
};

export function UserManagementPage() {
  const currentAdmin = useAdminAuthStore((state) => state.admin);
  const [admins, setAdmins] = useState<AdminWithRestaurants[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminWithRestaurants | null>(null);
  const [formData, setFormData] = useState<AdminFormData>(emptyFormData);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [adminsRes, restaurantsRes] = await Promise.all([
        superadminService.getAdmins(),
        superadminService.getRestaurants(),
      ]);
      setAdmins(adminsRes.admins);
      setRestaurants(restaurantsRes.restaurants);
    } catch {
      toast.error('Error al cargar los datos');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingAdmin(null);
    setFormData(emptyFormData);
    setShowDialog(true);
  };

  const openEditDialog = (admin: AdminWithRestaurants) => {
    setEditingAdmin(admin);
    setFormData({
      email: admin.email,
      password: '',
      name: admin.name,
      role: admin.role as AdminRole,
      super_admin: admin.super_admin || false,
      restaurant_ids: admin.restaurants?.map((r) => r.id) || [],
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.email.trim() || !formData.name.trim()) {
      toast.error('Email y nombre son requeridos');
      return;
    }

    if (!editingAdmin && !formData.password.trim()) {
      toast.error('La contraseña es requerida para nuevos usuarios');
      return;
    }

    setIsSaving(true);
    try {
      if (editingAdmin) {
        const updateData: { admin: Partial<AdminFormData>; restaurant_ids?: string[] } = {
          admin: {
            email: formData.email,
            name: formData.name,
            role: formData.role,
            super_admin: formData.super_admin,
          },
          restaurant_ids: formData.restaurant_ids,
        };
        if (formData.password.trim()) {
          updateData.admin.password = formData.password;
        }
        await superadminService.updateAdmin(editingAdmin.id, updateData as never);
        toast.success('Usuario actualizado correctamente');
      } else {
        await superadminService.createAdmin({
          admin: {
            email: formData.email,
            password: formData.password,
            name: formData.name,
            role: formData.role,
            super_admin: formData.super_admin,
          },
          restaurant_ids: formData.restaurant_ids,
        });
        toast.success('Usuario creado correctamente');
      }
      setShowDialog(false);
      loadData();
    } catch (error: unknown) {
      const apiError = error as { response?: { data?: { error?: string; details?: string[] } } };
      const message = apiError?.response?.data?.error ||
        (apiError?.response?.data?.details as string[])?.join(', ') ||
        'Error al guardar el usuario';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (admin: AdminWithRestaurants) => {
    if (admin.id === currentAdmin?.id) {
      toast.error('No puedes eliminar tu propia cuenta');
      return;
    }

    if (!confirm(`¿Estás seguro de eliminar al usuario "${admin.name}"?`)) {
      return;
    }

    try {
      await superadminService.deleteAdmin(admin.id);
      toast.success('Usuario eliminado correctamente');
      setAdmins(admins.filter((a) => a.id !== admin.id));
    } catch (error: unknown) {
      const apiError = error as { response?: { data?: { error?: string } } };
      toast.error(apiError?.response?.data?.error || 'Error al eliminar el usuario');
    }
  };

  const toggleRestaurant = (restaurantId: string) => {
    setFormData((prev) => ({
      ...prev,
      restaurant_ids: prev.restaurant_ids.includes(restaurantId)
        ? prev.restaurant_ids.filter((id) => id !== restaurantId)
        : [...prev.restaurant_ids, restaurantId],
    }));
  };

  if (isLoading) {
    return (
      <AdminPageShell>
        <div className="space-y-6">
          <PageHeader title="Gestión de Usuarios" />
          <PageLoading variant="list" rows={5} />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <PageHeader title="Gestión de Usuarios">
          <AdminButton variant="primary" onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Usuario
          </AdminButton>
        </PageHeader>

      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Nombre</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Email</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Rol</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Super Admin</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Restaurantes</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {admins.map((admin) => (
              <tr key={admin.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm">{admin.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{admin.email}</td>
                <td className="px-4 py-3 text-sm">
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                    {AdminRoleLabels[admin.role as AdminRole]}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  {admin.super_admin ? (
                    <ShieldCheck className="h-5 w-5 text-green-600" />
                  ) : (
                    <Shield className="h-5 w-5 text-gray-400" />
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {admin.restaurants?.map((r) => r.name).join(', ') || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(admin)}
                      className="h-8 w-8"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(admin)}
                      className="h-8 w-8 text-red-600 hover:text-red-700"
                      disabled={admin.id === currentAdmin?.id}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingAdmin ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
            <DialogDescription>
              {editingAdmin
                ? 'Modifica los datos del usuario.'
                : 'Crea un nuevo usuario en el sistema.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre completo"
                className="min-h-11"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@ejemplo.com"
                className="min-h-11"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">
                Contraseña {editingAdmin ? '(dejar vacío para mantener)' : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="min-h-11"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role">Rol</Label>
              <Select
                value={formData.role}
                onValueChange={(value: AdminRole) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className="min-h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Propietario</SelectItem>
                  <SelectItem value="manager">Gerente</SelectItem>
                  <SelectItem value="staff">Personal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="super_admin"
                checked={formData.super_admin}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, super_admin: checked as boolean })
                }
              />
              <Label htmlFor="super_admin" className="cursor-pointer">
                Super Administrador
              </Label>
            </div>
            <div className="grid gap-2">
              <Label>Restaurantes asignados</Label>
              <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                {restaurants.map((restaurant) => (
                  <div key={restaurant.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`restaurant-${restaurant.id}`}
                      checked={formData.restaurant_ids.includes(restaurant.id)}
                      onCheckedChange={() => toggleRestaurant(restaurant.id)}
                    />
                    <Label
                      htmlFor={`restaurant-${restaurant.id}`}
                      className="cursor-pointer text-sm font-normal"
                    >
                      {restaurant.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="min-h-11"
            >
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="min-h-11">
              {isSaving ? 'Guardando...' : editingAdmin ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminPageShell>
  );
}
